/**
 * Nova native UI primitives (M4.3): the mascot face + status pill shared by the
 * native board, builder sheet, and run-history screen. Re-exports the neutral
 * schedule/format helpers. Nova is the brand face per the WL-N1 rebrand.
 */
import React from 'react';
import { Image, StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { colors } from '@tabbio/tokens';
import { useTranslation } from 'react-i18next';
import type { NovaAutomationStatus } from '@tabbio/types';
import { useTheme } from '../../hooks/useTheme';
import { useFont } from '../../providers/FontProvider';
import { cronExpressionOf, eventSourceOf, formatWhen } from './nova-format';

export { cronExpressionOf, eventSourceOf, formatWhen };

// eslint-disable-next-line @typescript-eslint/no-require-imports
const NOVA_MASCOT = require('../../../assets/branding/nova-mascot.png') as number;

export function NovaMascot({ size = 40 }: { size?: number }) {
  return (
    <Image source={NOVA_MASCOT} style={{ width: size, height: size }} resizeMode="contain" />
  );
}

export function NovaStatusBadge({ status }: { status: NovaAutomationStatus | string }) {
  const { theme } = useTheme();
  const { fonts } = useFont();
  const { t } = useTranslation('common');

  const palette = (() => {
    switch (status) {
      case 'active':
        return { bg: `${theme.success}1F`, fg: theme.success };
      case 'draft':
        return { bg: `${colors.primary}1A`, fg: colors.primary };
      case 'paused':
      case 'archived':
      default:
        return { bg: theme.surfaceSecondary, fg: theme.textSecondary };
    }
  })();

  return (
    <View style={[styles.badge, { backgroundColor: palette.bg }]}>
      <View style={[styles.dot, { backgroundColor: palette.fg }]} />
      <Text style={[styles.badgeText, { color: palette.fg, fontFamily: fonts.medium }]}>
        {t(`nova.status.${status}`, { defaultValue: String(status) })}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 24,
    paddingHorizontal: 10,
    borderRadius: 9999,
    alignSelf: 'flex-start',
  },
  dot: { width: 7, height: 7, borderRadius: 4 },
  badgeText: { fontSize: 12, fontWeight: '600' },
});
